import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "../lib/api";
import { eur } from "../lib/format";
import { useLocale } from "../lib/useLocale";
import { useAuth } from "../context/AuthContext";
import JourneyWizard from "../components/JourneyWizard";
import {
  Check, ArrowRight, Loader2, Leaf, Sun, HeartPulse, Sprout, Waves,
  Compass, MapPin, Plus, Users2, Home, Sparkles, X, Wand2,
} from "lucide-react";

const WHY_ICONS = [Leaf, Sun, HeartPulse, Sprout, Waves];
const HEALING_VIDEO = "https://videos.pexels.com/video-files/27698122/12208143_1080_1920_60fps.mp4";
const HEALING_POSTER = "https://images.pexels.com/videos/27698122/abstract-abstract-art-abstract-design-abstract-patterns-27698122.jpeg?auto=compress&cs=tinysrgb&w=1600";

export default function RetreatExperiences() {
  const { t } = useTranslation();
  const { path: lp } = useLocale();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [reset, setReset] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);

  useEffect(() => {
    api.get("/experiences").then((r) => setReset((r.data || [])[0] || null)).catch(() => {});
  }, []);

  const bookFixed = () => {
    if (!user) return login(lp("/retreats"));
    setWizardOpen(true);
  };

  const why = t("exp.why", { returnObjects: true });
  const points = t("exp.cust.points", { returnObjects: true });
  const includesPreview = (reset?.includes || []).slice(0, 5);

  return (
    <div className="bg-rr-bg text-rr-ivory" data-testid="experiences-page">
      <Helmet>
        <title>{t("exp.metaTitle")}</title>
        <meta name="description" content={t("exp.metaDesc")} />
      </Helmet>

      {/* HERO */}
      <section className="relative overflow-hidden grain pt-24 md:pt-32 pb-12">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 80% at 50% 0%, rgba(201,164,107,0.10) 0%, transparent 55%)" }} />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center fade-up">
          <span className="overline">{t("exp.newHero.overline")}</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-4">
            {t("exp.newHero.title")} <span className="text-rr-gold">{t("exp.newHero.accent")}</span>
          </h1>
          <p className="text-rr-muted text-base md:text-lg max-w-2xl mx-auto mt-6 leading-relaxed">{t("exp.newHero.sub")}</p>
        </div>
      </section>

      {/* TWIN FLAGSHIP CARDS */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-16 grid lg:grid-cols-2 gap-6 items-stretch">
        {/* ROKERT 14 */}
        <article className="flex flex-col rounded-3xl border border-rr-gold/30 overflow-hidden" style={{ background: "var(--rr-surface)" }} data-testid="card-rokert-14">
          <div className="relative h-56 shrink-0">
            <img src={reset?.image || HEALING_POSTER} alt="ROKERT 14" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-rr-surface via-rr-surface/20 to-transparent" />
          </div>
          <div className="p-7 md:p-8 flex flex-col flex-1">
            <span className="overline">{t("exp.reset.overline")}</span>
            <h2 className="font-heading text-4xl text-rr-ivory mt-2">{reset?.name || "ROKERT 14"}</h2>
            <p className="text-rr-gold text-sm mt-2">{t("exp.reset.tagline")}</p>

            <div className="grid grid-cols-2 gap-3 mt-6">
              <div className="rounded-xl border border-rr-line p-4" data-testid="reset-price-shared">
                <span className="flex items-center gap-1.5 text-[11px] text-rr-muted"><Users2 className="w-3.5 h-3.5 text-rr-gold" /> {t("exp.reset.sharedLabel")}</span>
                <p className="font-heading text-2xl text-rr-ivory mt-1.5">{eur(reset?.price_from_eur || 2940)}</p>
                <p className="text-[11px] text-rr-muted">{t("exp.reset.perPerson")}</p>
              </div>
              <div className="rounded-xl border border-rr-gold/50 bg-rr-gold/[0.04] p-4" data-testid="reset-price-private">
                <span className="flex items-center gap-1.5 text-[11px] text-rr-muted"><Home className="w-3.5 h-3.5 text-rr-gold" /> {t("exp.reset.privateLabel")}</span>
                <p className="font-heading text-2xl text-rr-ivory mt-1.5">{eur(reset?.price_private_eur || 3276)}</p>
                <p className="text-[11px] text-rr-muted">{t("exp.reset.perPerson")}</p>
              </div>
            </div>

            <p className="overline mt-7 mb-3">{t("exp.reset.includesTitle")}</p>
            <ul className="space-y-2 flex-1">
              {includesPreview.map((inc) => (
                <li key={inc} className="flex items-start gap-2 text-sm text-rr-ivory/85"><Check className="w-4 h-4 text-rr-gold mt-0.5 shrink-0" strokeWidth={2} /> {inc}</li>
              ))}
              {(reset?.includes || []).length > includesPreview.length && (
                <li className="flex items-start gap-2 text-sm text-rr-muted"><Plus className="w-4 h-4 text-rr-gold/70 mt-0.5 shrink-0" /> +{reset.includes.length - includesPreview.length} more incl. yoga, meals, medicines & transfers</li>
              )}
            </ul>

            <button onClick={bookFixed} data-testid="reset-book-btn" className="mt-7 w-full btn-gold py-3.5 text-[11px] uppercase flex items-center justify-center gap-2">
              {t("land.flagship.cta")} <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-[11px] text-rr-muted/70 text-center mt-3 leading-relaxed">{t("exp.reset.invoiceNote")}</p>
          </div>
        </article>

        {/* ROKERT CUSTOM */}
        <article className="flex flex-col rounded-3xl border border-rr-gold/30 overflow-hidden" style={{ background: "var(--rr-surface)" }} data-testid="card-rokert-custom">
          <div className="relative h-56 shrink-0">
            <img src="https://images.unsplash.com/photo-1507525428034-b723cf961d3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="ROKERT CUSTOM" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-rr-surface via-rr-surface/20 to-transparent" />
          </div>
          <div className="p-7 md:p-8 flex flex-col flex-1">
            <span className="overline">{t("exp.cust.overline")}</span>
            <h2 className="font-heading text-4xl text-rr-ivory mt-2">{t("exp.cust.name")}</h2>
            <p className="text-rr-gold text-sm mt-2">{t("exp.cust.tagline")}</p>

            <div className="mt-6 rounded-xl border border-rr-gold/50 bg-rr-gold/[0.04] p-4 flex items-center gap-3" data-testid="custom-quote">
              <Wand2 className="w-5 h-5 text-rr-gold shrink-0" strokeWidth={1.5} />
              <div>
                <p className="font-heading text-2xl text-rr-ivory leading-none">{t("exp.cust.quote")}</p>
                <p className="text-[11px] text-rr-muted mt-1">{t("exp.bespoke.noPrice")}</p>
              </div>
            </div>

            <p className="overline mt-7 mb-3">{t("exp.bespoke.step4")}</p>
            <ul className="space-y-2 flex-1">
              {(Array.isArray(points) ? points : []).map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-rr-ivory/85"><Check className="w-4 h-4 text-rr-gold mt-0.5 shrink-0" strokeWidth={2} /> {p}</li>
              ))}
            </ul>

            <button onClick={() => setBuilderOpen(true)} data-testid="custom-open-btn" className="mt-7 w-full btn-outline py-3.5 text-[11px] uppercase flex items-center justify-center gap-2">
              {t("exp.cust.open")} <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-[11px] text-rr-muted/70 text-center mt-3 leading-relaxed">{t("exp.bespoke.intro")}</p>
          </div>
        </article>
      </section>

      {/* THE ISLAND OF HEALING — VIDEO */}
      <section className="relative h-[72vh] min-h-[460px] overflow-hidden" data-testid="healing-video-section">
        <video className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline poster={HEALING_POSTER}>
          <source src={HEALING_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(13,20,17,0.55), rgba(13,20,17,0.35) 45%, rgba(13,20,17,0.85))" }} />
        <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
          <span className="overline text-rr-gold">{t("exp.video.overline")}</span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-rr-ivory mt-4 max-w-3xl leading-[1.08]">{t("exp.video.title")}</h2>
          <p className="text-rr-ivory/75 text-base md:text-lg max-w-xl mt-5 leading-relaxed">{t("exp.video.sub")}</p>
        </div>
      </section>

      {/* WHY SRI LANKA (kept) */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-20">
        <div className="mb-12"><span className="overline">{t("exp.whyOverline")}</span><h2 className="font-heading text-4xl md:text-5xl mt-3">{t("exp.whyTitle")}</h2></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {(Array.isArray(why) ? why : []).map((w, i) => {
            const Icon = WHY_ICONS[i] || Leaf;
            return (
              <div key={w.title} className="rounded-2xl border border-rr-line p-7 hover:border-rr-gold/40 transition-colors">
                <span className="flex items-center justify-center w-12 h-12 rounded-full bg-rr-gold/12 border border-rr-gold/25"><Icon className="w-5 h-5 text-rr-gold" strokeWidth={1.5} /></span>
                <h3 className="font-heading text-2xl mt-5">{w.title}</h3>
                <p className="text-sm text-rr-muted mt-2 leading-relaxed">{w.copy}</p>
              </div>
            );
          })}
        </div>
      </section>

      {wizardOpen && (
        <JourneyWizard mode="create" preselectedExperience={reset?.slug}
          onClose={() => setWizardOpen(false)}
          onSaved={(id) => { setWizardOpen(false); navigate(lp(`/trips/${id}`)); }} />
      )}
      {builderOpen && <BespokeBuilder onClose={() => setBuilderOpen(false)} />}
    </div>
  );
}

function BespokeBuilder({ onClose }) {
  const { t } = useTranslation();
  const durations = t("exp.bespoke.durations", { returnObjects: true });
  const slExperiences = t("exp.bespoke.experiences", { returnObjects: true });
  const [packages, setPackages] = useState([]);
  const [duration, setDuration] = useState("");
  const [ayurveda, setAyurveda] = useState("");
  const [picked, setPicked] = useState([]);
  const [note, setNote] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data || [])).catch(() => {});
  }, []);

  const sortedPackages = [...packages].sort((a, b) => (b.bookable !== false) - (a.bookable !== false));

  const toggle = (x) => setPicked((p) => (p.includes(x) ? p.filter((i) => i !== x) : [...p, x]));

  const submit = async () => {
    if (!name.trim() || !email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return toast.error(t("exp.bespoke.formError"));
    }
    setSending(true);
    try {
      await api.post("/bespoke-enquiry", {
        name, email, phone, duration, ayurveda_experience: ayurveda,
        sri_lanka_experiences: picked, note,
      });
      setDone(true);
    } catch { toast.error("Could not send — please try again"); }
    finally { setSending(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-testid="bespoke-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl bg-rr-surface border border-rr-gold/20 shadow-[0_40px_100px_rgba(0,0,0,0.7)]">
        <div className="sticky top-0 z-10 bg-rr-surface/95 backdrop-blur px-6 md:px-8 py-5 border-b border-rr-line flex items-center justify-between">
          <div>
            <span className="overline">{t("exp.cust.overline")}</span>
            <p className="font-heading text-2xl text-rr-ivory mt-0.5">{t("exp.cust.name")}</p>
          </div>
          <button onClick={onClose} data-testid="bespoke-close" className="text-rr-muted hover:text-rr-ivory"><X className="w-6 h-6" /></button>
        </div>

        <div className="px-6 md:px-8 py-7">
          {done ? (
            <div className="text-center py-8" data-testid="bespoke-success">
              <Check className="w-10 h-10 text-rr-gold mx-auto" strokeWidth={1.4} />
              <h3 className="font-heading text-3xl text-rr-ivory mt-4">{t("exp.bespoke.successTitle")}</h3>
              <p className="text-rr-ivory/75 mt-3 max-w-md mx-auto leading-relaxed">{t("exp.bespoke.successBody")}</p>
              <button onClick={onClose} className="mt-7 btn-outline px-8 py-3 text-[11px] uppercase">{t("exp.bespoke.name2") && "Close"}</button>
            </div>
          ) : (
            <div className="space-y-9">
              <p className="text-rr-ivory/70 text-sm leading-relaxed -mt-1">{t("exp.bespoke.intro")}</p>

              <Step n={1} title={t("exp.bespoke.step1")} hint={t("exp.bespoke.step1hint")}>
                <div className="flex flex-wrap gap-2.5">
                  {(Array.isArray(durations) ? durations : []).map((d) => (
                    <Chip key={d} active={duration === d} onClick={() => setDuration(d)} testid={`bespoke-dur-${d}`}>{d}</Chip>
                  ))}
                </div>
              </Step>

              <Step n={2} title={t("exp.bespoke.step2")} hint={t("exp.bespoke.step2hint")}>
                <div className="grid sm:grid-cols-2 gap-3">
                  {sortedPackages.map((p) => {
                    const available = p.bookable !== false;
                    return (
                      <button key={p.slug} onClick={() => available && setAyurveda(p.name)} disabled={!available} aria-disabled={!available}
                        data-testid={`bespoke-ayur-${p.slug}`}
                        className={`relative text-left rounded-xl border p-4 flex items-center gap-3 transition-all overflow-hidden ${!available ? "opacity-45 cursor-not-allowed border-rr-line" : ayurveda === p.name ? "border-rr-gold bg-rr-gold/5" : "border-rr-line hover:border-rr-ivory/30"}`}>
                        <MapPin className="w-4 h-4 text-rr-gold shrink-0" />
                        <span className="min-w-0">
                          <span className="block text-rr-ivory text-sm">{p.name}</span>
                          <span className="block text-[11px] text-rr-muted">{p.area}</span>
                          {available && p.early_bird_discount_percent > 0 && (
                            <span data-testid={`bespoke-ayur-${p.slug}-earlybird`} className="inline-block mt-1 text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-rr-gold/15 text-rr-gold border border-rr-gold/30">
                              Early Bird −{p.early_bird_discount_percent}%
                            </span>
                          )}
                        </span>
                        {available && ayurveda === p.name && <Check className="w-4 h-4 text-rr-gold ml-auto" />}
                        {!available && (
                          <span data-testid={`bespoke-ayur-${p.slug}-ribbon`} className="absolute top-2 -right-7 w-28 rotate-45 bg-black/80 text-white text-[9px] uppercase tracking-widest text-center py-0.5 shadow-lg">
                            {t("exp.bespoke.notAvailable")}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </Step>

              <Step n={3} title={t("exp.bespoke.step3")} hint={t("exp.bespoke.step3hint")}>
                <div className="flex flex-wrap gap-2.5">
                  {(Array.isArray(slExperiences) ? slExperiences : []).map((x) => (
                    <Chip key={x} active={picked.includes(x)} onClick={() => toggle(x)} testid={`bespoke-exp-${x}`} icon={Compass}>{x}</Chip>
                  ))}
                </div>
              </Step>

              <Step n={4} title={t("exp.bespoke.step4")} hint={t("exp.bespoke.step4hint")}>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="bespoke-note"
                  placeholder={t("exp.bespoke.notePlaceholder")}
                  className="w-full bg-rr-bg/60 border border-rr-line rounded-xl px-4 py-3 text-sm text-rr-ivory focus:border-rr-gold outline-none" />
              </Step>

              <div className="rounded-2xl border border-rr-line bg-rr-bg/30 p-6">
                <p className="overline mb-4">{t("exp.bespoke.contactTitle")}</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("exp.bespoke.name2")} data-testid="bespoke-name" className="bg-rr-bg/60 border border-rr-line rounded-lg px-4 py-3 text-sm text-rr-ivory focus:border-rr-gold outline-none" />
                  <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("exp.bespoke.email")} data-testid="bespoke-email" className="bg-rr-bg/60 border border-rr-line rounded-lg px-4 py-3 text-sm text-rr-ivory focus:border-rr-gold outline-none" />
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("exp.bespoke.phone")} data-testid="bespoke-phone" className="bg-rr-bg/60 border border-rr-line rounded-lg px-4 py-3 text-sm text-rr-ivory focus:border-rr-gold outline-none" />
                </div>
                <button onClick={submit} disabled={sending} data-testid="bespoke-submit"
                  className="mt-5 btn-gold w-full py-3.5 flex items-center justify-center gap-2 text-xs uppercase">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t("exp.bespoke.submit")} <ArrowRight className="w-4 h-4" /></>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Step({ n, title, hint, children }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-rr-gold text-rr-bg font-heading text-sm shrink-0">{n}</span>
        <div>
          <h3 className="font-heading text-xl text-rr-ivory leading-tight">{title}</h3>
          {hint && <p className="text-[11px] text-rr-muted">{hint}</p>}
        </div>
      </div>
      <div className="pl-11">{children}</div>
    </div>
  );
}

function Chip({ active, onClick, children, testid, icon: Icon }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition-all ${active ? "border-rr-gold bg-rr-gold text-rr-bg" : "border-rr-line text-rr-ivory/85 hover:border-rr-gold/50"}`}>
      {Icon && <Icon className={`w-3.5 h-3.5 ${active ? "text-rr-bg" : "text-rr-gold"}`} />} {children}
    </button>
  );
}
